import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { FxProvider } from "@/context/FxContext";
import Layout from "@/components/Layout";
import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Landing from "@/pages/public/Landing";

// Code-split secondary routes so the initial bundle stays small.
const Expenses = lazy(() => import("@/pages/Expenses"));
const Incomes = lazy(() => import("@/pages/Incomes"));
const Categories = lazy(() => import("@/pages/Categories"));
const Budget = lazy(() => import("@/pages/Budget"));
const Profile = lazy(() => import("@/pages/Profile"));
const Insights = lazy(() => import("@/pages/Insights"));
const Recurring = lazy(() => import("@/pages/Recurring"));
const Goals = lazy(() => import("@/pages/Goals"));
const Debts = lazy(() => import("@/pages/Debts"));
const Investments = lazy(() => import("@/pages/Investments"));
const Workspaces = lazy(() => import("@/pages/Workspaces"));
const Admin = lazy(() => import("@/pages/Admin"));
const About = lazy(() => import("@/pages/About"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Help = lazy(() => import("@/pages/Help"));

// Public marketing pages (code-split)
const Features = lazy(() => import("@/pages/public/Features"));
const HowItWorks = lazy(() => import("@/pages/public/HowItWorks"));
const FAQ = lazy(() => import("@/pages/public/FAQ"));
const Blog = lazy(() => import("@/pages/public/Blog"));
const BlogPost = lazy(() => import("@/pages/public/BlogPost"));

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function RouteFallback() {
  return (
    <div className="flex h-[60vh] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/**
 * Smart index route:
 *  - unauthenticated  → public marketing landing (with PublicLayout)
 *  - authenticated    → redirect to /dashboard (authenticated app shell)
 *
 * The public marketing routes below use PublicLayout so the top nav and footer
 * are consistent. Legal pages (About / Privacy / Terms / Help) also live under
 * PublicLayout so they're crawlable and accessible without login — the ONLY
 * hard-locked routes are the actual finance dashboard and its sub-pages.
 */
/**
 * Smart index route:
 *  - unauthenticated  → Navigate to /auth (Login/Sign-in)
 *  - authenticated    → Navigate to /dashboard
 *
 * The public Landing page and all other public routes (/features, /blog, etc.)
 * remain reachable via direct URL — this only changes what "/" resolves to.
 */
function RootRoute() {
  const { user } = useAuth();
  if (user === null) return <RouteFallback />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/auth" replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <FxProvider>
            <BrowserRouter>
              <Routes>
                {/* Auth screens — no ads, no marketing chrome */}
                <Route
                  path="/auth"
                  element={
                    <PublicOnly>
                      <Auth />
                    </PublicOnly>
                  }
                />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Root dispatcher — TOP-LEVEL (no PublicLayout wrapper) so
                    the public marketing shell never paints for unauthenticated
                    visitors on "/". Landing/PublicLayout are still available
                    via direct URLs like /features, /blog, /about, etc. */}
                <Route path="/" element={<RootRoute />} />

                {/* Public marketing + legal (crawlable, ad-eligible) */}
                <Route element={<PublicLayout />}>
                  <Route path="/features" element={<Suspense fallback={<RouteFallback />}><Features /></Suspense>} />
                  <Route path="/how-it-works" element={<Suspense fallback={<RouteFallback />}><HowItWorks /></Suspense>} />
                  <Route path="/faq" element={<Suspense fallback={<RouteFallback />}><FAQ /></Suspense>} />
                  <Route path="/blog" element={<Suspense fallback={<RouteFallback />}><Blog /></Suspense>} />
                  <Route path="/blog/:slug" element={<Suspense fallback={<RouteFallback />}><BlogPost /></Suspense>} />
                  <Route path="/about" element={<Suspense fallback={<RouteFallback />}><About /></Suspense>} />
                  <Route path="/privacy" element={<Suspense fallback={<RouteFallback />}><Privacy /></Suspense>} />
                  <Route path="/terms" element={<Suspense fallback={<RouteFallback />}><Terms /></Suspense>} />
                  <Route path="/help" element={<Suspense fallback={<RouteFallback />}><Help /></Suspense>} />
                </Route>

                {/* Authenticated app */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/expenses" element={<Suspense fallback={<RouteFallback />}><Expenses /></Suspense>} />
                  <Route path="/incomes" element={<Suspense fallback={<RouteFallback />}><Incomes /></Suspense>} />
                  <Route path="/insights" element={<Suspense fallback={<RouteFallback />}><Insights /></Suspense>} />
                  <Route path="/recurring" element={<Suspense fallback={<RouteFallback />}><Recurring /></Suspense>} />
                  <Route path="/goals" element={<Suspense fallback={<RouteFallback />}><Goals /></Suspense>} />
                  <Route path="/debts" element={<Suspense fallback={<RouteFallback />}><Debts /></Suspense>} />
                  <Route path="/investments" element={<Suspense fallback={<RouteFallback />}><Investments /></Suspense>} />
                  <Route path="/workspaces" element={<Suspense fallback={<RouteFallback />}><Workspaces /></Suspense>} />
                  <Route path="/admin" element={<Suspense fallback={<RouteFallback />}><Admin /></Suspense>} />
                  <Route path="/categories" element={<Suspense fallback={<RouteFallback />}><Categories /></Suspense>} />
                  <Route path="/budget" element={<Suspense fallback={<RouteFallback />}><Budget /></Suspense>} />
                  <Route path="/profile" element={<Suspense fallback={<RouteFallback />}><Profile /></Suspense>} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </FxProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
